import { Controller } from 'stimulus'
import * as analytics from '../lib/analytics.ts'
import { getQueryParametersString } from '../lib/query_param_utils.ts'
import { AccountForm } from './form_account_controller'
import { AddressForm } from './form_address_controller'
import { ChildEligibilityForm } from './form_child_eligibility_controller.ts'
import { ParentEligibilityForm } from './form_parent_eligibility_controller.ts'
import { PlansForm } from './form_plans_controller'

export type SubmitD2cResponse = {
  success: boolean
  hashedSignupData: string
  hashedProfileId: string
  totalPrice: number
  redirect_to: string
}

export default class CheckoutController extends Controller {
  accountForm: AccountForm | null = null
  addressForm: AddressForm | null = null
  childEligibilityForm: ChildEligibilityForm | null = null // Child eligibility single child flow
  childEligibilityForms: ChildEligibilityForm[] | null = null // Child eligibility with siblings flow
  parentEligibilityForm: ParentEligibilityForm | null = null
  plansForm: PlansForm | null = null

  loading = false

  static outlets = ['analytics']

  declare submitUrlValue: string
  declare partnerIdValue: string
  declare eligibilityTypeValue: 'child' | 'parent'
  declare hasPartnerIdValue: boolean
  declare submitPartnerUrlValue: string
  static values = {
    submitUrl: String,
    partnerId: String,
    eligibilityType: String,
    submitPartnerUrl: String,
  }

  onPage1() {
    if (this.hasPartnerIdValue) {
      analytics.trackPartnerPage1(this.partnerIdValue)
    } else {
      analytics.trackPage1()
    }
  }

  onPage2() {
    if (!this.accountForm) return

    if (this.hasPartnerIdValue) {
      analytics.trackPartnerPage2(
        this.partnerIdValue,
        this.eligibilityTypeValue,
        this.accountForm,
      )
    } else {
      analytics.trackPage2(this.accountForm)
    }
  }

  onPage3() {
    if (!this.accountForm || !this.addressForm) return

    if (this.hasPartnerIdValue) {
      analytics.trackPartnerPage3(this.partnerIdValue, this.accountForm)
    } else {
      analytics.trackPage3(this.accountForm, this.addressForm)
    }
  }

  setAccountForm(accountForm: AccountForm) {
    this.accountForm = accountForm
  }

  setAddressForm(addressForm: AddressForm) {
    this.addressForm = addressForm
  }

  setChildEligibilityForm(childEligibilityForm: ChildEligibilityForm) {
    this.childEligibilityForm = childEligibilityForm
  }

  setChildEligibilityForms(childEligibilityForms: ChildEligibilityForm[]) {
    this.childEligibilityForms = childEligibilityForms
  }

  setParentEligibilityForm(parentEligibilityForm: ParentEligibilityForm) {
    this.parentEligibilityForm = parentEligibilityForm
  }

  setPlanForm(planForm: PlansForm) {
    this.plansForm = planForm
  }

  async submitD2c() {
    if (this.loading) return
    this.loading = true

    if (!this.accountForm) throw new Error('Missing account form')
    if (!this.addressForm) throw new Error('Missing address form')
    if (!this.plansForm) throw new Error('Missing plan form')

    try {
      const url = `${this.submitUrlValue}?${getQueryParametersString()}`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account: this.accountForm,
          address: this.addressForm,
          plans: this.plansForm,
          full_url: window.location.href,
        }),
      })

      const data: SubmitD2cResponse = await response.json()
      if (!data.success) {
        throw new Error('checkout failure')
      }

      void analytics.trackSuccess({
        submitResponse: data,
        accountForm: this.accountForm,
        addressForm: this.addressForm,
        plansForm: this.plansForm,
      })
      window.location.href = encodeURI(data.redirect_to)
    } catch (error) {
      this.loading = false
      throw error
    }
  }

  async submitPartner() {
    if (this.loading) return
    this.loading = true

    if (!this.accountForm) throw new Error('Missing account form')
    if (
      this.eligibilityTypeValue === 'child' &&
      !this.childEligibilityForm &&
      !this.childEligibilityForms
    )
      throw new Error('Missing child eligibility form(s)')
    if (this.eligibilityTypeValue === 'parent' && !this.parentEligibilityForm)
      throw new Error('Missing parent eligibility form')
    if (!this.addressForm) throw new Error('Missing address form')

    try {
      const url = `${this.submitPartnerUrlValue}?${getQueryParametersString()}`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account: this.accountForm,
          child_eligibilities: this.childEligibilityForm
            ? [this.childEligibilityForm]
            : this.childEligibilityForms,
          parent_eligibility: this.parentEligibilityForm,
          address: this.addressForm,
          full_url: window.location.href,
        }),
      })

      const data: SubmitD2cResponse = await response.json()
      void analytics.trackPartnerSuccess({
        partnerId: this.partnerIdValue,
        accountForm: this.accountForm,
        addressForm: this.addressForm,
      })
      window.location.pathname = data.redirect_to
    } catch (error) {
      this.loading = false
      throw error
    }
  }
}
